/**
 * IMPORTS
 */

import { useNavigate } from "react-router-dom";

import { Trash, Undo2 } from "lucide-react";

// components
import { Button } from "@components/button/button";

// pages
import { ModalConfirmacaoRemessa } from "@pages/client/details-model/components/modal-confirmacao";
import { TermModalRequestShipmentLoose } from "@pages/client/request-shipment-loose/components/term-modal";
import { useRequestShipmentLooseViewModel } from "@pages/client/request-shipment-loose/view-model";

const RequestShipmentLoosePage = () => {
  const navigate = useNavigate();

  const {
    modeloUnico,
    obligatory,
    metadadosCamposVariaveis,
    dadosRemessa,
    avulsaMatricula,
    avulsaCampos,
    avulsaImagemPreview,
    avulsaImagemInputRef,
    isModalSolicitacaoRemessa,
    isOpenTermModal,
    isLoadingSolicitarRemessa,
    setAvulsaMatricula,
    setAvulsaImagem,
    setAvulsaImagemPreview,
    setIsModalSolicitacaoRemessa,
    setIsOpenTermModal,
    handleAvulsaImagemSelect,
    handleAvulsaCampoChange,
    handleAvulsaSubmit,
    handleConfirmarSolicitacao,
    handleSolicitarRemessa,
    handleSubmitResponsabilidade,
    handleCleanState,
  } = useRequestShipmentLooseViewModel();

  if (!modeloUnico) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 text-sm text-gray-800">
        <div className="rounded bg-white p-6 shadow">
          <h1 className="mb-2 text-xl font-semibold">Solicitacao avulsa</h1>
          <p className="mb-4 text-sm text-gray-600">
            Nenhum modelo selecionado. Volte para a tela de modelos e selecione um modelo para
            continuar.
          </p>
          <Button onClick={() => navigate("/")}>Voltar para modelos</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 text-sm text-gray-800">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="mb-2 text-xl font-semibold">
            Solicitacao avulsa: {modeloUnico?.nome_modelo}
          </h1>
          <p className="text-lg font-bold text-red-600">
            OBS: esse modulo aceita apenas 2 solicitacoes avulsas por dia.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="default"
            className="h-8 gap-1 text-neutral-50"
            onClick={() => {
              handleCleanState();
              navigate("/");
            }}
          >
            <Undo2 className="h-3.5 w-3.5" />
            <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">Voltar</span>
          </Button>

          <Button
            size="sm"
            variant="destructive"
            className="h-8 gap-1 text-neutral-50"
            onClick={handleCleanState}
          >
            <Trash className="h-3.5 w-3.5" />
            <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">Limpar dados</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded bg-white p-4 shadow">
          <h2 className="mb-2 font-semibold text-red-600">SOLICITACAO AVULSA:</h2>
          <p className="mb-4 text-xs">
            Favor preencher os dados abaixo e clicar em Solicitar.
            <br />
            <strong>OBS:</strong> Esse modulo aceita apenas 2 solicitacoes avulsas por dia.
          </p>

          <form className="space-y-3" onSubmit={handleAvulsaSubmit}>
            <div>
              <label className="mb-1 block">
                {obligatory === "undefined" ? "N/A" : obligatory}:
              </label>
              <input
                className="w-full rounded border border-gray-300 p-2"
                type="text"
                value={avulsaMatricula}
                onChange={(event) => setAvulsaMatricula(event.target.value)}
                required
              />
            </div>

            {Number(modeloUnico?.modeloCompleto?.tem_carga_foto) === 1 && (
              <div>
                <label className="mb-1 block">Foto:</label>
                <div className="flex flex-col space-y-2">
                  <button
                    type="button"
                    className="w-full rounded bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
                    onClick={() => avulsaImagemInputRef.current?.click()}
                  >
                    Selecionar imagem...
                  </button>

                  <input
                    ref={avulsaImagemInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvulsaImagemSelect}
                  />

                  {avulsaImagemPreview && (
                    <div className="mt-2 flex items-center space-x-2">
                      <img
                        src={avulsaImagemPreview}
                        alt="Preview"
                        className="h-16 w-16 rounded border object-cover"
                      />
                      <button
                        type="button"
                        className="text-xs text-red-500"
                        onClick={() => {
                          if (avulsaImagemPreview) {
                            URL.revokeObjectURL(avulsaImagemPreview);
                          }
                          setAvulsaImagem(null);
                          setAvulsaImagemPreview(null);
                        }}
                      >
                        Remover
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {metadadosCamposVariaveis?.map((metadadoCampo, index) => (
              <div key={index}>
                <label className="mb-1 block">
                  {metadadoCampo.nome}
                  {metadadoCampo.obrigatorio ? "" : " (opcional)"}:
                </label>
                <input
                  className="w-full rounded border border-gray-300 p-2"
                  type="text"
                  value={avulsaCampos[metadadoCampo.nome] || ""}
                  onChange={(event) =>
                    handleAvulsaCampoChange(metadadoCampo.nome, event.target.value)
                  }
                  required={metadadoCampo.obrigatorio}
                />
              </div>
            ))}

            <button
              type="submit"
              className="rounded bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              Preparar solicitacao avulsa
            </button>
          </form>
        </div>

        <div className="rounded bg-white p-4 shadow">
          <h2 className="mb-4 font-semibold">Resumo da solicitacao</h2>

          <div className="mb-4 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
            <p>
              Registros prontos para envio: <strong>{dadosRemessa?.length ?? 0}</strong>
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Apos preparar os dados avulsos, use o botao abaixo para solicitar a remessa.
            </p>
          </div>

          <Button
            size="default"
            variant="default"
            className="h-8 w-full gap-1 text-neutral-50"
            onClick={handleConfirmarSolicitacao}
          >
            <span className="sr-only text-neutral-50 sm:not-sr-only sm:whitespace-nowrap">
              Solicitar remessa avulsa
            </span>
          </Button>
        </div>
      </div>

      <TermModalRequestShipmentLoose
        isOpen={isOpenTermModal}
        onClose={() => setIsOpenTermModal(false)}
        onSubmitResponsabilidade={handleSubmitResponsabilidade}
      />

      {isModalSolicitacaoRemessa && (
        <ModalConfirmacaoRemessa
          isPending={isLoadingSolicitarRemessa}
          onCancel={() => setIsModalSolicitacaoRemessa(false)}
          confirmarSolicitacao={handleSolicitarRemessa}
        />
      )}
    </div>
  );
};

/**
 * EXPORTS
 */
export { RequestShipmentLoosePage };
